import { useState } from 'react';
import { RequestFailed } from '../api/client';
import type { Person } from '../api/types';
import { PersonSearch } from './PersonSearch';

interface Props {
  /** Те, с кем уже есть переписка. Общего списка участников нет. */
  contacts: Person[];
  onSearch: (query: string) => Promise<Person[]>;
  onCreate: (title: string, memberIds: string[]) => Promise<unknown>;
  onCancel: () => void;
}

export function NewGroupPanel({ contacts, onSearch, onCreate, onCancel }: Props) {
  const [title, setTitle] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /** Найденные поиском — их нет в адресной книге, но добавить в группу можно. */
  const [extra, setExtra] = useState<Person[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const candidates = [...contacts, ...extra.filter((p) => !contacts.some((c) => c.id === p.id))];

  function toggle(userId: string) {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  }

  function addFound(person: Person) {
    setExtra((previous) => previous.some((p) => p.id === person.id) ? previous : [...previous, person]);
    setSelected((previous) => new Set(previous).add(person.id));
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await onCreate(title.trim(), [...selected]);
    } catch (e) {
      setError(e instanceof RequestFailed ? e.error.message : 'Не удалось создать группу');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel">
      <form onSubmit={submit}>
        <div className="field">
          <label htmlFor="groupTitle">Название группы</label>
          <input
            id="groupTitle"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Например, Семья"
            maxLength={128}
            autoFocus
            required
          />
        </div>

        <div className="panel-list">
          {candidates.length === 0 && (
            <p className="panel-hint">
              Некого добавить из знакомых. Найдите человека поиском ниже.
            </p>
          )}
          {candidates.map((person) => (
            <label className="check" key={person.id}>
              <input
                type="checkbox"
                checked={selected.has(person.id)}
                onChange={() => toggle(person.id)}
              />
              <span>{person.displayName}</span>
            </label>
          ))}
        </div>

        {/* Группу без участников создать можно: людей добавляют и позже */}
        <div className="panel-actions">
          <button type="submit" disabled={busy || !title.trim()}>
            {busy ? 'Создаём…' : 'Создать'}
          </button>
          <button type="button" className="ghost" onClick={onCancel}>Отмена</button>
        </div>

        {error && <p className="error">{error}</p>}
      </form>

      <div className="panel-search">
        <div className="section-title" style={{ padding: '10px 0 2px' }}>Добавить по имени</div>
        <PersonSearch onSearch={onSearch} onPick={addFound} actionLabel="добавить" />
      </div>
    </div>
  );
}
